import { z } from 'zod';
import { InvoiceItemKind, InvoiceStatus } from '../enums.js';
import { egp } from './expenses.dto.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const InvoiceItemInputSchema = z.object({
  kind: z.enum(InvoiceItemKind).default('OTHER'),
  description: z.string().min(1).max(300),
  quantity: z.number().positive().max(999_999).multipleOf(0.01).default(1),
  unitPriceEgp: z.number().min(0).max(99_999_999).multipleOf(0.01),
  /** Link to the rebilled expense (kind REBILLED_EXPENSE). */
  expenseId: z.string().uuid().nullable().optional(),
});
export type InvoiceItemInput = z.infer<typeof InvoiceItemInputSchema>;

export const CreateInvoiceSchema = z.object({
  customerId: z.string().uuid(),
  tripId: z.string().uuid().nullable().optional(),
  issueDate: isoDate.optional(),
  dueDate: isoDate.nullable().optional(),
  /** Defaults to the tenant VAT rate (14%). */
  taxRatePct: z.number().min(0).max(100).multipleOf(0.01).optional(),
  notes: z.string().max(1000).nullable().optional(),
  items: z.array(InvoiceItemInputSchema).min(1).max(100),
});
export type CreateInvoice = z.infer<typeof CreateInvoiceSchema>;

export const UpdateInvoiceSchema = CreateInvoiceSchema.partial();
export type UpdateInvoice = z.infer<typeof UpdateInvoiceSchema>;

/** Builds a DRAFT from a completed trip: freight line (+ optional per-km line) + rebillable approved expenses. */
export const InvoiceFromTripSchema = z.object({
  freightEgp: egp.nullable().optional(),
  ratePerKmEgp: z.number().positive().max(99_999).multipleOf(0.01).nullable().optional(),
  includeRebillableExpenses: z.boolean().default(true),
  dueInDays: z.number().int().min(0).max(365).default(30),
});
export type InvoiceFromTrip = z.infer<typeof InvoiceFromTripSchema>;

export const MarkInvoicePaidSchema = z.object({
  /** Defaults to the outstanding balance. */
  amountEgp: egp.optional(),
  paidAt: z.string().datetime({ offset: true }).optional(),
});
export type MarkInvoicePaid = z.infer<typeof MarkInvoicePaidSchema>;

export const InvoicesQuerySchema = z.object({
  status: z.enum(InvoiceStatus).optional(),
  customerId: z.string().uuid().optional(),
  tripId: z.string().uuid().optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type InvoicesQuery = z.infer<typeof InvoicesQuerySchema>;

/** Driver-app: a scanned/paper invoice handed over at delivery. */
export const DriverInvoiceUploadSchema = z.object({
  fileId: z.string().uuid(),
  tripId: z.string().uuid().nullable().optional(),
  customerId: z.string().uuid().nullable().optional(),
  amountEgp: egp.nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
});
export type DriverInvoiceUpload = z.infer<typeof DriverInvoiceUploadSchema>;

/** Allowed UI actions per status. */
export const INVOICE_TRANSITIONS: Record<InvoiceStatus, readonly ('update' | 'send' | 'pay' | 'cancel' | 'delete' | 'pdf')[]> = {
  DRAFT: ['update', 'send', 'pay', 'cancel', 'delete', 'pdf'],
  SENT: ['pay', 'cancel', 'pdf'],
  OVERDUE: ['pay', 'cancel', 'pdf'],
  PAID: ['pdf'],
  CANCELLED: [],
};

/** Money helpers shared by API and UIs: half-up rounding to 2 decimals. */
export const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

export function computeInvoiceTotals(items: { quantity: number; unitPriceEgp: number }[], taxRatePct: number) {
  const lines = items.map((i) => round2(i.quantity * i.unitPriceEgp));
  const subtotalEgp = round2(lines.reduce((a, b) => a + b, 0));
  const taxEgp = round2((subtotalEgp * taxRatePct) / 100);
  return { lines, subtotalEgp, taxEgp, totalEgp: round2(subtotalEgp + taxEgp) };
}
